import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { playSound, speak } from '../utils/sounds';
import { getApiUrl, getExerciseEndpoint } from '../config/api';
import { getSuccessMessage, getErrorMessage } from '../utils/encouragements';
import RewardCounter from '../components/RewardCounter';

const LectureExercise = () => {
  const { level } = useParams();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState(null);
  const [attempts, setAttempts] = useState(3);
  const [feedback, setFeedback] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [recognizedText, setRecognizedText] = useState('');
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const lastRecognizedRef = React.useRef('');
  const hasSpokenInstructionsRef = React.useRef(false);

  const initSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recog = new SpeechRecognition();
      recog.lang = 'fr-FR';
      recog.continuous = true;  // Continue d'écouter
      recog.interimResults = true;  // Résultats intermédiaires pour affichage
      recog.maxAlternatives = 1;

      recog.onstart = () => {
        console.log('🎤 Speech recognition started');
        setRecognizedText(''); // Réinitialiser
      };

      recog.onresult = (event) => {
        console.log('📝 onresult called, results count:', event.results.length);

        // Prendre le dernier résultat
        const lastResult = event.results[event.results.length - 1];
        const transcript = lastResult[0].transcript;
        const spoken = transcript.toUpperCase().replace(/\s/g, '');
        console.log('🗣️ Speech recognized:', transcript, '→', spoken, 'isFinal:', lastResult.isFinal);

        // Stocker dans une ref ET dans le state
        lastRecognizedRef.current = spoken;
        setRecognizedText(spoken);
        console.log('State and ref updated with:', spoken);
      };

      recog.onspeechstart = () => {
        console.log('👂 Speech detected');
      };

      recog.onspeechend = () => {
        console.log('🔇 Speech ended');
      };

      recog.onerror = (event) => {
        console.error('❌ Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'no-speech') {
          setFeedback({ type: 'error', message: 'Je n\'ai rien entendu. Parle plus fort !' });
        } else if (event.error === 'not-allowed') {
          setFeedback({ type: 'error', message: 'Autorise le microphone dans ton navigateur !' });
        } else if (event.error === 'aborted') {
          // Ignoré, c'est normal quand on arrête manuellement
          console.log('Recognition aborted (normal)');
        } else {
          setFeedback({ type: 'error', message: 'Je n\'ai pas bien entendu. Réessaie !' });
        }
      };

      recog.onend = () => {
        console.log('⏹️ Speech recognition ended');
        setIsListening(false);
      };

      setRecognition(recog);
      console.log('✅ Speech recognition initialized');
    } else {
      console.error('Speech recognition not supported in this browser');
      setFeedback({ type: 'error', message: 'Ton navigateur ne supporte pas la reconnaissance vocale. Utilise Chrome !' });
    }
  };

  const loadExercise = useCallback(async () => {
    try {
      const endpoint = getExerciseEndpoint('lecture', level);
      const url = getApiUrl(endpoint);
      console.log('Loading exercise from:', url);
      const response = await axios.get(url);
      console.log('Exercise loaded:', response.data);
      setExercise(response.data);
      setAttempts(3);
      setFeedback(null);
      setRecognizedText(''); // Réinitialiser le texte reconnu
      lastRecognizedRef.current = ''; // Réinitialiser la ref
      setIsListening(false); // Arrêter l'écoute si active
      if (recognition) {
        try {
          recognition.stop();
        } catch (e) {
          // Ignorer si déjà arrêté
        }
      }
      hasSpokenInstructionsRef.current = false; // Réinitialiser pour le nouvel exercice
      setTimeout(() => {
        if (!hasSpokenInstructionsRef.current) {
          speak(response.data.instructions);
          hasSpokenInstructionsRef.current = true;
        }
      }, 500);
    } catch (error) {
      console.error('Error loading exercise:', error);
      console.error('Error details:', error.response || error.message);
      setExercise({ syllable: 'ERROR', instructions: 'Erreur de chargement. Vérifiez la console.' });
    }
  }, [level, recognition]);

  useEffect(() => {
    loadExercise();
    initSpeechRecognition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  const startListening = (isRestart = false) => {
    console.log('startListening called, recognition:', !!recognition, 'attempts:', attempts, 'isListening:', isListening, 'isRestart:', isRestart);

    // Empêcher de démarrer si déjà en cours d'écoute (sauf si c'est un recommencer)
    if (isListening && !isRestart) {
      console.log('Already listening, ignoring start request');
      return;
    }

    if (recognition && attempts > 0) {
      // Réinitialiser l'état
      setFeedback(null);
      setRecognizedText('');
      lastRecognizedRef.current = '';

      // Arrêter toute reconnaissance en cours avant de démarrer
      try {
        recognition.stop();
      } catch (e) {
        // Ignorer si pas démarrée
      }

      // Attendre un peu avant de redémarrer (surtout important pour "Recommencer")
      setTimeout(() => {
        setIsListening(true);
        speak('Je t\'écoute !');

        setTimeout(() => {
          try {
            console.log('Starting speech recognition...');
            recognition.start();
          } catch (error) {
            console.error('Error starting recognition:', error);
            setIsListening(false);
            // Si l'erreur est "already started", réessayer après l'arrêt
            if (error.message && error.message.includes('already started')) {
              setTimeout(() => {
                try {
                  recognition.start();
                } catch (retryError) {
                  console.error('Retry failed:', retryError);
                  setFeedback({ type: 'error', message: 'Erreur de démarrage du micro. Réessaie !' });
                }
              }, 300);
            } else {
              setFeedback({ type: 'error', message: 'Erreur de démarrage du micro. Réessaie !' });
            }
          }
        }, 1000);
      }, isRestart ? 200 : 0);
    } else {
      console.log('Cannot start listening:', { recognition: !!recognition, attempts });
    }
  };

  const validateAnswer = () => {
    // Arrêter l'écoute si elle est en cours
    if (recognition && isListening) {
      recognition.stop();
      setIsListening(false);
    }

    if (recognizedText) {
      console.log('Validating:', recognizedText);
      checkAnswer(recognizedText);
    } else {
      setFeedback({ type: 'error', message: 'Je n\'ai rien entendu !' });
    }
  };

  const normalizePhonetic = (text) => {
    // Normalisation phonétique pour gérer les sons qui se prononcent pareil
    let normalized = text.toUpperCase();

    // K, C, Q se prononcent souvent pareil (à la fin des mots aussi)
    normalized = normalized.replace(/[CQK]/g, 'K');

    // Autres équivalences phonétiques courantes
    normalized = normalized.replace(/PH/g, 'F');
    normalized = normalized.replace(/[EÉÈ]/g, 'E');

    return normalized;
  };

  const checkAnswer = (spoken) => {
    console.log('✓ checkAnswer called with:', spoken);
    if (!exercise) {
      console.log('❌ No exercise loaded');
      return;
    }

    const expected = exercise.syllable.toUpperCase();

    // Si la syllabe attendue a 2 lettres, prendre les 2 premières lettres du texte reconnu
    const spokenToCompare = expected.length <= 2 ? spoken.substring(0, 2) : spoken;

    // Normalisation phonétique pour la comparaison
    const normalizedSpoken = normalizePhonetic(spokenToCompare);
    const normalizedExpected = normalizePhonetic(expected);

    // Vérification : correspondance exacte OU correspondance phonétique OU le texte reconnu commence par la syllabe attendue
    const isCorrect = spokenToCompare === expected ||
                     normalizedSpoken === normalizedExpected ||
                     spoken.startsWith(expected);

    console.log('Comparing:', { spoken, spokenToCompare, expected, normalizedSpoken, normalizedExpected, isCorrect });

    if (isCorrect) {
      console.log('✅ Correct answer!');
      playSound('success');
      setCorrectCount(prev => prev + 1);
      const childName = localStorage.getItem('childName');
      const successMessage = getSuccessMessage(childName);
      setFeedback({
        type: 'success',
        message: '🎉 ✨ Bravo ! C\'est la bonne réponse ! ⭐'
      });
      speak(successMessage);
      setTimeout(() => {
        loadExercise();
      }, 3000);
    } else {
      console.log('❌ Wrong answer');
      const newAttempts = attempts - 1;
      setAttempts(newAttempts);
      playSound('error');

      if (newAttempts > 0) {
        const childName = localStorage.getItem('childName');
        const errorMessage = getErrorMessage(childName);
        setFeedback({
          type: 'error',
          message: `❌ Pas tout à fait ! Il te reste ${newAttempts} essai${newAttempts > 1 ? 's' : ''}.`
        });
        speak(errorMessage);
      } else {
        setIncorrectCount(prev => prev + 1);
        const fullMessage = `La bonne réponse était ${expected}`;
        setFeedback({
          type: 'error',
          message: `❌ La bonne réponse était "${expected}". On passe au suivant !`
        });
        speak(fullMessage);
        // Calculer le délai en fonction de la longueur du message (environ 100ms par caractère)
        const delay = Math.max(4000, fullMessage.length * 100);
        setTimeout(() => {
          loadExercise();
        }, delay);
      }
    }
  };

  if (!exercise) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div>
      <RewardCounter correctCount={correctCount} incorrectCount={incorrectCount} />

      <div className="header">
        <h1>📖 Lecture - Niveau {level}</h1>
        <p>{exercise.instructions}</p>
      </div>

      <div className="card">
        <div className={`syllable-display ${feedback?.type === 'success' ? 'celebrate' : feedback?.type === 'error' ? 'shake' : ''}`}>
          {exercise.syllable}
        </div>

        <div className="attempts">
          {[...Array(3)].map((_, i) => (
            <span key={i} className="attempt-heart">
              {i < attempts ? '❤️' : '🖤'}
            </span>
          ))}
        </div>

        {feedback && (
          <div className={`feedback ${feedback.type === 'success' ? 'feedback-success' : 'feedback-error'}`}>
            {feedback.message}
          </div>
        )}

        {recognizedText && (
          <div style={{
            textAlign: 'center',
            marginBottom: '30px',
            fontSize: '2.5rem',
            color: '#2F80ED',
            fontWeight: 'bold',
            animation: isListening ? 'pulse 1s infinite' : 'none'
          }}>
            Tu dis : <span style={{ color: '#FF6B6B' }}>
              {exercise && exercise.syllable ? recognizedText.substring(0, exercise.syllable.length) : recognizedText}
            </span>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '30px', display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {!recognizedText ? (
            <button
              className={`btn btn-primary ${isListening ? 'pulse' : 'bounce'}`}
              onClick={startListening}
              disabled={attempts === 0 || isListening}
              style={{
                fontSize: '2rem',
                padding: '30px 60px',
                opacity: attempts === 0 || isListening ? 0.6 : 1
              }}
            >
              {isListening ? '🎤 J\'écoute...' : '🎤 Prononce le son'}
            </button>
          ) : (
            <>
              <button
                className="btn btn-success celebrate"
                onClick={validateAnswer}
                style={{
                  fontSize: '2rem',
                  padding: '30px 60px'
                }}
              >
                ✅ Valider
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => startListening(true)}
                style={{
                  fontSize: '1.5rem',
                  padding: '20px 40px'
                }}
              >
                🔄 Recommencer
              </button>
            </>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '40px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          <button
            className="btn btn-warning"
            onClick={() => navigate('/subjects')}
          >
            🏠 Retour
          </button>
          <button
            className="btn btn-success"
            onClick={loadExercise}
          >
            ⏭️ Suivant
          </button>
        </div>
      </div>
    </div>
  );
};

export default LectureExercise;
